import React, { useState, useEffect, Fragment } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import Button from '~~elements/Button';
import Modal from '~~elements/Modal';
import Input from '~~elements/Input';
import toast from '~~elements/Toast';
import Multilingual from '~~utils/Multilingual';
import { SeasonResource } from '~~apis/resource';
import GameMediaSection from './GameMediaSection';
import { useTournamentState, useTournamentDispatch } from '../useTournament';
import {
  updateGameMedia,
  updateGameNews,
  deleteGameNews,
  updateGameRelatedLinks,
  validScoreBoardData,
  updateGameScoreBoard,
} from '../service';
import GameNewsSection from './GameNewsSection';
import GameRelatedLinksSection from './GameRelatedLinksSection';
import GameScoreBoardSection from './GameScoreBoardSection';
import moment from 'moment';

const ModalGameDiv = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  font-size: 0.9rem;
  .basic-info {
    display: grid;
    grid-template-columns: 1fr 2fr;
    > * {
      border-bottom: 1px solid #eee;
      margin: 0;
      padding: 0.25rem 0.5rem;
      &.row-gap {
        margin-bottom: 1rem;
      }
    }
  }
`;

const ScheduleGameModal = ({ tournament, selectedGame, handleCancelGameSelect, modalGame }) => {
  const { organizerUniqid, games } = useTournamentState();
  const tournamentDispatch = useTournamentDispatch();

  const [relatedLinks, setRelatedLinks] = useState([]);

  const [quickEditOptions, setQuickEditOptions] = useState(false);
  const [quickEditMode, setQuickEditMode] = useState('');
  const [quickEditData, setQuickEditData] = useState(null);
  const [quickEditApiInProgress, setQuickEditApiInProgress] = useState(false);

  const [tryDelete, setTryDelete] = useState(false);
  const [gameDeleteInput, setGameDeleteInput] = useState('');
  const [gameDeleteInProgress, setGameDeleteInProgress] = useState(false);

  useEffect(() => {
    setQuickEditOptions(false);
    setQuickEditMode('');
    setTryDelete(false);
    setGameDeleteInProgress(false);
    setQuickEditApiInProgress(false);

    if (!modalGame || !modalGame.related_links) {
      setRelatedLinks([]);
      return;
    }
    setRelatedLinks(modalGame.related_links);
  }, [modalGame]);

  const handleQuickEdit = (display) => () => {
    setQuickEditOptions(display);
  };

  const handleQuickEditMode = (mode) => () => {
    setQuickEditMode(mode);
    setQuickEditApiInProgress(false);
  };

  const handleQuickEditSubmit = () => {
    if (!quickEditData) {
      return;
    }

    let targetMethod = null;
    let targetData = null;
    let afterSuccess = () => {};
    switch (quickEditMode) {
      case 'SCORE_BOARD': {
        const { error, message, data: validData } = validScoreBoardData(
          tournament.auto_game_seq,
          games,
          modalGame.seq,
          quickEditData,
        );
        if (error) {
          toast(message, { status: 'error' });
          return;
        }
        targetMethod = updateGameScoreBoard;
        targetData = validData;
        afterSuccess = (newGameData) => {
          tournamentDispatch({
            type: 'UPDATE_GAME',
            payload: {
              ...newGameData,
            },
          });
        };
        break;
      }
      case 'MEDIA':
        targetMethod = updateGameMedia;
        targetData = quickEditData;
        afterSuccess = (newMediaList) => {
          tournamentDispatch({
            type: 'UPDATE_GAME',
            payload: {
              ...modalGame,
              media: newMediaList,
            },
          });
        };
        break;
      case 'NEWS':
        if (!quickEditData.title && !quickEditData.image_path && !quickEditData.content && modalGame.news) {
          targetMethod = deleteGameNews;
          targetData = quickEditData;
          afterSuccess = () => {
            tournamentDispatch({
              type: 'UPDATE_GAME',
              payload: {
                ...modalGame,
                news: undefined,
              },
            });
          };
        } else if (!quickEditData.title || !quickEditData.image_path || !quickEditData.content) {
          toast(Multilingual(`TOURNAMENT.ERROR.GAME_NEWS_REQUIREMENT`), { status: 'error' });
          return;
        } else {
          targetMethod = updateGameNews;
          targetData = quickEditData;
          afterSuccess = (newNews) => {
            tournamentDispatch({
              type: 'UPDATE_GAME',
              payload: {
                ...modalGame,
                news: newNews,
              },
            });
          };
        }
        break;
      case 'RELATED_LINKS':
        targetMethod = updateGameRelatedLinks;
        targetData = quickEditData;
        afterSuccess = (newRelatedLinks) => {
          tournamentDispatch({
            type: 'UPDATE_GAME',
            payload: {
              ...modalGame,
              related_links: newRelatedLinks,
            },
          });
        };
        break;
      default:
        return;
    }

    if (!targetMethod) {
      return;
    }

    setQuickEditApiInProgress(true);
    targetMethod(tournament.uniqid, modalGame.uniqid, targetData)
      .then((newMediaList) => {
        toast(Multilingual(`SUCCESS.UPDATE`), { status: 'success' });
        afterSuccess(newMediaList);
      })
      .catch(() => {
        toast(Multilingual(`TOURNAMENT.ERROR.UNKNOWN`), { status: 'error' });
      })
      .finally(() => {
        setQuickEditApiInProgress(false);
      });
  };

  const handleGameDelete = () => {
    if (!tryDelete) {
      setTryDelete(true);
      setGameDeleteInput('');
      return;
    }

    const correctInput = `G${modalGame.seq}`;
    if (gameDeleteInput !== correctInput) {
      toast(Multilingual(`ERROR.WRONG_INPUT`), { status: 'error' });
      return;
    }

    setGameDeleteInProgress(true);
    SeasonResource.deleteGame({ seasonUniqid: tournament.uniqid, gameUniqid: modalGame.uniqid })
      .then(() => {
        refetchGames().then(() => {
          toast(Multilingual(`SUCCESS.DELETE`), { status: 'success', second: 2 });
          handleCancelGameSelect();
        });
      })
      .catch((e) => {
        console.error(e);
        toast(Multilingual(`TOURNAMENT.ERROR.UNKNOWN`), { status: 'error' });
        setGameDeleteInProgress(false);
      });
  };

  const refetchGames = () => {
    return new Promise((resolve) => {
      SeasonResource.getGames({ seasonUniqid: tournament.uniqid })
        .then(({ data: games }) => {
          tournamentDispatch({ type: 'SET_GAMES', payload: games });
          resolve();
        })
        .catch((e) => {
          window.location.reload();
        });
    });
  };

  const handleGameDeleteInput = (e) => {
    setGameDeleteInput(e.target.value);
  };

  const cancelGameDelete = () => {
    setTryDelete(false);
  };

  const getFinishedGameInfo = () => {
    const finishedStatusFragment = (
      <Fragment>
        <h4>{Multilingual(`GAME.FINISHED_STATUS.WORDING`)}</h4>
        <label>{Multilingual(`GAME.FINISHED_STATUS.${modalGame.info.finished_status}`)}</label>
      </Fragment>
    );

    if (modalGame.info.finished_status === 'SUSPENDED') {
      return finishedStatusFragment;
    }

    let matchResultWording = `${modalGame.away.runs}:${modalGame.home.runs} `;
    switch (modalGame.info.winner_side) {
      case 'AWAY':
        matchResultWording += `- ${modalGame.away.team} ${Multilingual(`GAME.MATCH_RESULT.WIN`)}`;
        break;
      case 'HOME':
        matchResultWording += `- ${modalGame.home.team} ${Multilingual(`GAME.MATCH_RESULT.WIN`)}`;
        break;
      case 'TIE':
        matchResultWording += Multilingual(`GAME.MATCH_RESULT.DRAW`);
        break;
      default:
        return finishedStatusFragment;
    }

    return (
      <Fragment>
        {finishedStatusFragment}
        <h4>{Multilingual(`GAME.MATCH_RESULT.WORDING`)}</h4>
        <label>{matchResultWording}</label>
      </Fragment>
    );
  };

  const getBody = () => {
    if (quickEditMode === 'SCORE_BOARD') {
      return (
        <GameScoreBoardSection game={modalGame} onChange={setQuickEditData} apiInProgress={quickEditApiInProgress} />
      );
    }

    if (quickEditMode === 'MEDIA') {
      return (
        <GameMediaSection
          isPremium={tournament.config.expand_media_space}
          mediaList={!modalGame.media ? [] : modalGame.media}
          onChange={setQuickEditData}
          apiInProgress={quickEditApiInProgress}
        />
      );
    }

    if (quickEditMode === 'NEWS') {
      return (
        <GameNewsSection
          news={!modalGame.news ? null : modalGame.news}
          onChange={setQuickEditData}
          apiInProgress={quickEditApiInProgress}
        />
      );
    }

    if (quickEditMode === 'RELATED_LINKS') {
      return (
        <GameRelatedLinksSection
          relatedLinks={relatedLinks}
          onChange={setQuickEditData}
          apiInProgress={quickEditApiInProgress}
        />
      );
    }

    return (
      <ModalGameDiv>
        <div className="basic-info">
          <h4>{Multilingual(`GAME.AWAY`)}</h4>
          <label>{modalGame.away.team}</label>
          <h4 className="row-gap">{Multilingual(`GAME.HOME`)}</h4>
          <label className="row-gap">{modalGame.home.team}</label>
          <h4>{Multilingual(`GAME.STATUS.WORDING`)}</h4>
          <label>{Multilingual(`GAME.STATUS.${modalGame.info.status}`)}</label>
          {modalGame.info.status === 'FINISHED' && getFinishedGameInfo()}
          <h4>{Multilingual(`GAME.SCHEDULED_START_AT`)}</h4>
          <label>
            {moment(modalGame.info.time.scheduled_start_at).isValid()
              ? moment(modalGame.info.time.scheduled_start_at).format('YYYY/MM/DD HH:mm')
              : '-'}
          </label>
          <h4>{Multilingual(`GAME.STARTED_AT`)}</h4>
          <label>
            {moment(modalGame.info.time.started_at).isValid()
              ? moment(modalGame.info.time.started_at).format('YYYY/MM/DD HH:mm')
              : '-'}
          </label>
          <h4 className="row-gap">{Multilingual(`GAME.ENDED_AT`)}</h4>
          <label className="row-gap">
            {moment(modalGame.info.time.ended_at).isValid()
              ? moment(modalGame.info.time.ended_at).format('YYYY/MM/DD HH:mm')
              : '-'}
          </label>
          <h4>{Multilingual(`GAME.LOCATION`)}</h4>
          <label>{modalGame.info.location}</label>
          <h4>{Multilingual(`GAME.NEWS.WORDING`)}</h4>
          <label>{!modalGame.news ? 'X' : 'O'}</label>
          <h4>{Multilingual(`GAME.MEDIA`)}</h4>
          <label>{!modalGame.media || modalGame.media.length === 0 ? 'X' : 'O'}</label>
          <h4>{Multilingual(`GAME.RELATED_LINKS.WORDING`)}</h4>
          <label>{!modalGame.related_links || modalGame.related_links.length === 0 ? 'X' : 'O'}</label>
          <h4>{Multilingual(`GAME.STATS_COMPLETENESS.WORDING`)}</h4>
          <label>{Multilingual(`GAME.STATS_COMPLETENESS.${modalGame.info.stats_completeness}`)}</label>
        </div>
      </ModalGameDiv>
    );
  };

  const getFooter = () => {
    if (tryDelete) {
      return (
        <div style={{ width: '100%', overflowX: 'auto', textAlign: 'center' }}>
          <label style={{ fontSize: '0.8rem' }}>
            {Multilingual(`TOURNAMENT.GAME_DELETE_HINT`)}
            <Input
              width="5rem"
              placeholder={`G${modalGame.seq}`}
              value={gameDeleteInput}
              onChange={handleGameDeleteInput}
            />
          </label>
          <Button width="unset" nowrap color="red" onClick={handleGameDelete} disabled={gameDeleteInProgress}>
            {Multilingual(`COMMON.DELETE_CONFIRM`)}
          </Button>
          <Button
            width="unset"
            nowrap
            color="transparentWithBorder"
            onClick={cancelGameDelete}
            disabled={gameDeleteInProgress}
          >
            {Multilingual(`COMMON.CANCEL`)}
          </Button>
        </div>
      );
    }

    if (quickEditOptions) {
      if (quickEditMode !== '') {
        return (
          <Fragment>
            {quickEditData && (
              <Button
                width="unset"
                nowrap
                color="red"
                onClick={handleQuickEditSubmit}
                disabled={quickEditApiInProgress}
              >
                {Multilingual(`COMMON.UPDATE`)}
              </Button>
            )}
            <Button
              width="unset"
              nowrap
              color="transparentWithBorder"
              onClick={handleQuickEditMode('')}
              disabled={quickEditApiInProgress}
            >
              {Multilingual(`COMMON.BACK`)}
            </Button>
          </Fragment>
        );
      }

      return (
        <Fragment>
          <Button width="unset" nowrap color="red" onClick={handleQuickEditMode('SCORE_BOARD')}>
            {Multilingual(`GAME.SCORE_BOARD`)}
          </Button>
          <Button width="unset" nowrap color="red" onClick={handleQuickEditMode('MEDIA')}>
            {Multilingual(`GAME.MEDIA`)}
          </Button>
          <Button width="unset" nowrap color="red" onClick={handleQuickEditMode('NEWS')}>
            {Multilingual(`GAME.NEWS.WORDING`)}
          </Button>
          <Button width="unset" nowrap color="red" onClick={handleQuickEditMode('RELATED_LINKS')}>
            {Multilingual(`GAME.RELATED_LINKS.WORDING`)}
          </Button>
          <Button width="unset" nowrap color="transparentWithBorder" onClick={handleQuickEdit(false)}>
            {Multilingual(`COMMON.BACK`)}
          </Button>
        </Fragment>
      );
    }

    return (
      <Fragment>
        <Button width="unset" color="red" nowrap onClick={handleQuickEdit(true)}>
          {Multilingual(`COMMON.QUICK_EDIT`)}
        </Button>
        <Link
          to={(location) => ({
            ...location,
            pathname: `/organizer/${organizerUniqid}/tournament/${tournament.uniqid}/game`,
            search: `gid=${modalGame.uniqid}`,
          })}
        >
          <Button width="unset" color="red" nowrap>
            {Multilingual(`COMMON.EDIT`)}
          </Button>
        </Link>
        <a
          href={`${process.env.REACT_APP_MAINPAGE_URL}/season/${tournament.uniqid}?game=${modalGame.uniqid}&tab=box`}
          target="_blank"
          rel="noopener noreferrer"
        >
          <Button width="unset" color="blue" nowrap>
            {Multilingual(`COMMON.VIEW`)}
          </Button>
        </a>
        <Button width="unset" mini nowrap color="transparent" onClick={handleGameDelete}>
          {Multilingual(`COMMON.DELETE`)}
        </Button>
      </Fragment>
    );
  };

  return (
    <Modal.Modal
      size="lg"
      isOpen={!!selectedGame}
      outterClickBack={!gameDeleteInProgress && !quickEditMode}
      onClickBack={handleCancelGameSelect}
    >
      <Modal.ModalHeader
        hasBack={!gameDeleteInProgress && !quickEditApiInProgress}
        onClickBack={handleCancelGameSelect}
      >
        {`G${modalGame.seq} / ${modalGame.group}`}
      </Modal.ModalHeader>
      <Modal.ModalBody>{getBody()}</Modal.ModalBody>
      <Modal.ModalFooter>{getFooter()}</Modal.ModalFooter>
    </Modal.Modal>
  );
};

export default ScheduleGameModal;
